import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IKorviuInputErrorMessage,
  KorviuInputComponent
} from '../../../../shared/components/korviu-input/korviu-input.component';
import { KorviuTextareaComponent } from '../../../../shared/components/korviu-textarea/korviu-textarea.component';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { TranslateModule } from '@ngx-translate/core';
import { finalize, ReplaySubject, takeUntil, tap } from 'rxjs';
import { IGetPaginatedResponse } from '../../../../shared/types/get-paginated-response.interface';
import { InvitationTemplate } from 'core/types/invitation.interface';
import { User, UserTypes } from '../../../../shared/types/user.model';
import { TemplatesService } from 'core/services/templates.service';
import { IPagination } from '../../../../shared/types/pagination.interface';
import { IReferral } from 'core/types/referral.interface';
import { AuthService } from 'core/services/auth.service';
import { KorviuLoaderComponent } from '../../../../shared/components/korviu-loader/korviu-loader.component';
import {
  buildReferralSignupDeepLink,
  replaceReferralTemplatePlaceholders
} from '../../../../shared/utils/referral-template-placeholders.utils';

import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-default-users-form',
  standalone: true,
  imports: [
    FormsModule,
    KorviuInputComponent,
    KorviuTextareaComponent,
    NzRadioComponent,
    NzRadioGroupComponent,
    TranslateModule,
    ReactiveFormsModule,
    KorviuLoaderComponent
  ],
  templateUrl: './default-users-form.component.html',
  styleUrl: './default-users-form.component.scss'
})
export class DefaultUsersFormComponent implements OnInit, OnDestroy {
  @Input() modalUserType: UserTypes;
  @Input() set prefillData(value: IReferral) {
    this.referralForm.patchValue(value);
  }

  public readonly firstNameErrors: IKorviuInputErrorMessage[] = [
    { errorCode: 'required', localizedMessage: 'referral.validation.firstNameRequired' }
  ];
  public readonly lastNameErrors: IKorviuInputErrorMessage[] = [
    { errorCode: 'required', localizedMessage: 'referral.validation.lastNameRequired' }
  ];
  public readonly phoneNumberErrors: IKorviuInputErrorMessage[] = [
    { errorCode: 'required', localizedMessage: 'referral.validation.phoneNumberRequired' },
    { errorCode: 'pattern', localizedMessage: 'validation.invalidPhoneNumber' },
    { errorCode: 'minlength', localizedMessage: 'validation.invalidPhoneNumber' }
  ];

  public isLoading: boolean;
  public readonly referralForm = new FormGroup({
    id: new FormControl<number | null>(null),
    first_name: new FormControl(''),
    last_name: new FormControl(''),
    phone_number: new FormControl(''),
    country_code: new FormControl(''),
    type: new FormControl('whatsapp'),
    language: new FormControl('es'),
    live_in_us: new FormControl(true),
    message_template: new FormControl(''),
    contact_name: new FormControl(' '),
    email: new FormControl('')
  });

  @Output() onAddReferral: EventEmitter<IReferral>;

  public readonly firstNameControl = this.referralForm.controls.first_name;
  public readonly lastNameControl = this.referralForm.controls.last_name;
  public readonly phoneNumberPrefixControl = this.referralForm.controls.country_code;
  public readonly phoneNumberControl = this.referralForm.controls.phone_number;
  public readonly liveInUSControl = this.referralForm.controls.live_in_us;
  public readonly contactMethodControl = this.referralForm.controls.type;
  public readonly emailControl = this.referralForm.controls.email;
  public readonly languageControl = this.referralForm.controls.language;
  public readonly messageTemplateControl = this.referralForm.controls.message_template;

  public get currentUser(): User {
    return this._authService?.currentUser;
  }

  public get isValid(): boolean {
    return this.referralForm?.valid;
  }

  private _pagination: IPagination;
  private _onDestroy$: ReplaySubject<void>;
  private _selectedMessageTemplate: string;

  constructor(
    private _templatesService: TemplatesService,
    private _authService: AuthService
  ) {
    this._onDestroy$ = new ReplaySubject<void>(1);
    this.onAddReferral = new EventEmitter<IReferral>();
    this._pagination = { page: 1, pageSize: 10 };
    this._selectedMessageTemplate = '';
    this._setFormListeners();
  }

  public ngOnInit() {
    this._filterAndSetMessageTemplate();
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public onPhoneNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
    this.phoneNumberControl.setValue(input.value);
  }

  public validateAndSubmit(): void {
    this._setDefaultValidators();
    for (const control of Object.values(this.referralForm.controls)) {
      control.markAsDirty();
    }

    if (this.referralForm.valid) {
      this.onAddReferral.emit(this.referralForm.value as IReferral);
    }
  }

  private _setFormListeners(): void {
    this.firstNameControl.valueChanges.pipe(debounceTime(1000), takeUntil(this._onDestroy$)).subscribe(() => {
      this._filterAndSetMessageTemplate();
    });

    this.contactMethodControl.valueChanges
      .pipe(
        tap(() => this._setDefaultValidators()),
        debounceTime(1000),
        takeUntil(this._onDestroy$)
      )
      .subscribe(() => this._filterAndSetMessageTemplate({ preserveManualMessage: true }));

    this.languageControl.valueChanges.pipe(debounceTime(1000), takeUntil(this._onDestroy$)).subscribe(() => {
      this._filterAndSetMessageTemplate();
    });
  }

  private _filterAndSetMessageTemplate(options: { preserveManualMessage?: boolean } = {}): void {
    this.isLoading = true;
    const preserveManualMessage = options.preserveManualMessage && this._hasManualMessageEdit();
    const liveInUS: string = this.liveInUSControl.value ? 'us' : 'mx';
    const contactMethod: string = this.contactMethodControl.value;
    const language: string = this.languageControl.value;

    const filters: any = {
      language: language,
      location: liveInUS,
      contactMethod: contactMethod
    };

    if (this.modalUserType === UserTypes.SD) {
      filters.sms_template_type__name = 'initial_invitation_regular';
      filters.name = `initial_invitation_regular_${language}`;
    }

    this._templatesService
      .getReferralTemplate(this._pagination, filters, contactMethod)
      .pipe(
        tap((response: IGetPaginatedResponse<InvitationTemplate[]>) => {
          const selectedTemplate: InvitationTemplate = response.results[0];
          if (selectedTemplate) {
            this._selectedMessageTemplate = this._replacePlaceholders(
              selectedTemplate.body,
              this.referralForm.value,
              this.currentUser as User
            );

            if (!preserveManualMessage) {
              this.messageTemplateControl.setValue(this._selectedMessageTemplate);
              this.messageTemplateControl.markAsPristine();
            }
          }
        }),
        finalize(() => (this.isLoading = false)),
        takeUntil(this._onDestroy$)
      )
      .subscribe();
  }

  private _replacePlaceholders(template: string, formData: any, user: User): string {
    const referralCode = user.referralCode;

    return replaceReferralTemplatePlaceholders(template, {
      referralFirstName: this.firstNameControl.value || formData.first_name,
      referringMemberName: user.firstName,
      referralCode,
      signupDeepLink: buildReferralSignupDeepLink(window.location.origin, referralCode)
    });
  }

  private _hasManualMessageEdit(): boolean {
    return this.messageTemplateControl.dirty && this.messageTemplateControl.value !== this._selectedMessageTemplate;
  }

  private _setDefaultValidators(): void {
    this.firstNameControl.setValidators([Validators.required]);
    this.lastNameControl.setValidators([Validators.required]);
    this.contactMethodControl.setValidators([Validators.required]);
    this.languageControl.setValidators([Validators.required]);
    const isEmail = this.contactMethodControl.value === 'email';
    this.emailControl.setValidators(isEmail ? [Validators.required, Validators.email] : []);
    this.phoneNumberControl.setValidators(
      isEmail ? [] : [Validators.required, Validators.pattern('^[0-9]*$'), Validators.minLength(10)]
    );
    this.phoneNumberPrefixControl.setValidators(isEmail ? [] : [Validators.required]);

    for (const control of Object.values(this.referralForm.controls)) {
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
    this.referralForm.updateValueAndValidity({ emitEvent: false });
  }
}
